import { PageWrapper } from '@components'
import { DYNAMIC_LABELS, LABELS, TOOLTIPS } from '@constants'
import { useIsMobile } from '@hooks/useIsMobile'
import { useStore } from '@hooks/useStore'
import { Check, Download, Edit, Save, Email, ContentCopy, SwapHoriz, Visibility } from '@mui/icons-material'
import InvoiceIcon from '@mui/icons-material/ReceiptLong'
import { Box, Button, Tooltip, Typography, Skeleton } from '@mui/material'
import { ISingleDocument, QuotationStatus } from '@typings'
import { observer } from 'mobx-react-lite'
import React, { useEffect, useMemo, useState } from 'react'
import { FieldValues, useForm } from 'react-hook-form'
import { toast } from 'react-toastify'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { yupResolver } from '@hookform/resolvers/yup'
import { documentValidationSchema } from 'src/partials/forms/invoice/validation/document-line.form.validation'
import { OvFormProvider } from 'src/components/core/ov-form-provider/ov-form-provider'
import { SendEmailForm } from 'src/partials/forms/email'
import { QuotationStatusChangeDialog, QuotationStatusHistory } from 'src/components/features/quotation-status'
import { StatusBadge } from 'src/components/features/status-badge'
import { RelatedDocuments } from 'src/components/features/document-relationships'
import { DocumentPreviewModal } from 'src/components/features/document-preview'

import { UpdateInvoiceForm } from 'src/partials/index.partials'

export const QuotationDetailView = observer(() => {
  const { ui, quotation, shared, client, company, billing } = useStore()
  const isMobile = useIsMobile()
  const [edit, setEdit] = useState(false)
  const [emailFormOpen, setEmailFormOpen] = useState(false)
  const [statusDialogOpen, setStatusDialogOpen] = useState(false)
  const [statusHistory, setStatusHistory] = useState<any[]>([])
  const finalized = useMemo(() => quotation.single.is_finalised, [quotation.single.is_finalised])
  const isEditable = useMemo(() => quotation.single.is_editable, [quotation.single.is_editable])
  const isStatusTerminal = useMemo(
    () =>
      ['accepted', 'rejected', 'invoiced'].includes(
        quotation.single.quotation_status || ''
      ),
    [quotation.single.quotation_status]
  )
  const navigate = useNavigate()
  const { id } = useParams()
  const [searchParams, setSearchParams] = useSearchParams()

  const pageTitle = useMemo(
    () =>
      quotation.single.document_number
        ? DYNAMIC_LABELS.QUOTATION_NR(quotation?.single?.document_number)
        : LABELS.QUOTATION,
    [quotation?.single?.document_number]
  )

  const getDefaultValues = async () => {
    return await quotation.getSingle(parseInt(id ?? ''))
  }

  const form = useForm({
    resolver: yupResolver(documentValidationSchema),
    mode: 'all',
    defaultValues: getDefaultValues,
  })

  // Subscribe to form state to enable dirty tracking and error tracking
  // React-hook-form's formState is lazily evaluated - we need to subscribe to it
  React.useEffect(() => {
    const subscription = form.watch(() => {
      // Access formState to trigger tracking
      form.formState.isDirty
      form.formState.errors
      form.formState.isValid
    })
    return () => subscription.unsubscribe()
  }, [form])

  const handleBack = () => navigate('..')

  const handleSuccess = (newDocument: ISingleDocument) => {
    setEdit(false)
    form.reset(newDocument)
    ui.handleToaster('Wijzigingen opgeslagen', { type: 'success' })
  }

  const handleErrors = (errors: Record<string, string[]>, message?: string) => {
    if (!errors) return

    form.clearErrors()
    const errorKeys = Object.keys(errors)

    errorKeys.forEach(singleKey => {
      // Handle nested errors like "lines.0.label" without type casting
      const errorMessages = errors[singleKey]
      if (errorMessages && Array.isArray(errorMessages) && errorMessages.length > 0) {
        form.setError(singleKey as any, {
          type: 'manual',
          message: errorMessages[0],
        })
      }
    })

    if (message) toast(message, { type: 'error' })
  }

  const onSubmitCallback = async (values: FieldValues) => {
    await quotation
      .update(values)
      .then(res => {
        handleSuccess(res)
      })
      .catch((e: any) => {
        handleErrors(e.errors, e.message)
      })
  }

  const handleSubmit = () => form.handleSubmit(onSubmitCallback)()

  const handleCancel = () => {
    form.reset()
    setEdit(false)
  }

  const handleFinalize = async () => {
    try {
      const res = await quotation.finalise()
      form.reset(res)
    } catch (e: any) {
      if (!e) return
      ui.handleToaster(e.message || 'Er is iets misgegaan bij het definitief maken van de offerte', { type: 'error' })
    }
  }

  const handleDuplicate = async () => {
    try {
      const duplicatedQuotation = await quotation.duplicate(parseInt(id ?? ''))
      ui.handleToaster('Offerte gedupliceerd', { type: 'success' })
      navigate(`/offertes/${duplicatedQuotation.id}?edit=true`)
    } catch (e: any) {
      ui.handleToaster(e.message || 'Er is iets misgegaan bij het dupliceren', { type: 'error' })
    }
  }

  const handlePreview = async () => {
    await ui.showModal({
      title: 'Voorvertoning',
      body: () => <DocumentPreviewModal onLoadPreview={() => quotation.previewPdf()} />,
      modalOptions: {
        maxWidth: 'xl',
        fullWidth: true,
      },
    }).catch(() => void 0)
  }

  const handleConvertToInvoice = async () => {
    try {
      const newInvoice = await quotation.convertToInvoice(parseInt(id ?? ''))
      ui.handleToaster('Factuur aangemaakt vanuit offerte', { type: 'success' })
      navigate(`/facturen/${newInvoice.id}?edit=true`)
    } catch (e: any) {
      ui.handleToaster(e.message || 'Er is iets misgegaan bij het omzetten naar factuur', { type: 'error' })
    }
  }

  const handleSendEmail = async () => {
    // Check if quotation has already been sent
    if (quotation.single.quotation_status === 'sent') {
      try {
        await ui.confirm({
          title: 'E-mail al verstuurd',
          content:
            'Deze offerte is al eerder verstuurd. Bekijk de verzonden e-mail in je logboek onder ' +
            'Instellingen → E-mail. Wil je een nieuwe e-mail opstellen?',
          confirmLabel: 'Verder',
          cancelLabel: 'Annuleren',
          withCancel: true,
        })
        // User confirmed, open the email form
        setEmailFormOpen(true)
      } catch {
        // User cancelled, do nothing
      }
    } else {
      // Not sent yet, open the email form directly
      setEmailFormOpen(true)
    }
  }

  // Skeleton loading component for buttons
  const ButtonsSkeleton = () => (
    <Box
      display="flex"
      flexDirection={isMobile ? 'column' : 'row'}
      gap={1}
      width={isMobile ? '100%' : 'auto'}>
      {isMobile ? (
        <>
          <Skeleton variant="rounded" width="100%" height={36} />
          <Skeleton variant="rounded" width="100%" height={36} />
          <Skeleton variant="rounded" width="100%" height={36} />
        </>
      ) : (
        <>
          <Skeleton variant="rounded" width={120} height={36} />
          <Skeleton variant="rounded" width={120} height={36} />
          <Skeleton variant="rounded" width={120} height={36} />
        </>
      )}
    </Box>
  )

  const handleStatusChange = async (newStatus: QuotationStatus, comment?: string) => {
    try {
      const updatedQuotation = await quotation.updateQuotationStatus(newStatus, comment)
      form.reset(updatedQuotation)
      ui.handleToaster('Offerte status bijgewerkt', { type: 'success' })
      // Refresh status history after status change
      fetchStatusHistory()
    } catch (e: any) {
      throw new Error(e.message || 'Er is iets misgegaan bij het wijzigen van de status')
    }
  }

  // Check for edit parameter in URL and automatically enter edit mode
  useEffect(() => {
    const shouldEdit = searchParams.get('edit') === 'true'
    if (shouldEdit && isEditable && !edit) {
      setEdit(true)
      // Clean up the URL parameter after setting edit mode
      searchParams.delete('edit')
      setSearchParams(searchParams, { replace: true })
    }
  }, [searchParams, isEditable, edit, setSearchParams])

  useEffect(() => {
    if (edit) {
      if (!client.has_clients) client.getAll()
      if (!shared.has_countries) shared.getCountries()
    }
    if (!shared.has_vat_codes) shared.getVatCodes()
    // Ensure company data is loaded for VAT code filtering
    if (!company.current_company) company.getMyCompany()
  }, [edit, shared.has_countries, shared.has_vat_codes, shared.has_clients, company])

  // Fetch status history when quotation is finalized
  useEffect(() => {
    const fetchStatusHistory = async () => {
      if (finalized && id) {
        try {
          console.log('Fetching status history for quotation:', id)
          const history = await quotation.getQuotationStatusHistory(parseInt(id))
          console.log('Status history received:', history)
          setStatusHistory(history)
        } catch (e) {
          console.error('Failed to fetch status history:', e)
        }
      }
    }

    fetchStatusHistory()
  }, [finalized, id, quotation])

  const fetchStatusHistory = async () => {
    if (finalized && id) {
      try {
        const history = await quotation.getQuotationStatusHistory(parseInt(id))
        setStatusHistory(history)
      } catch (e) {
        console.error('Failed to fetch status history:', e)
      }
    }
  }

  return (
    <OvFormProvider
      loading={quotation.loading}
      form={form}
      readOnly={!edit}
      onSubmit={handleSubmit}
      onCancel={edit ? handleCancel : undefined}>
      <PageWrapper
        backAction={handleBack}
        pageTitle={
          <Box
            display="flex"
            alignItems="center"
            gap={1.5}>
            <Typography variant="h4">{pageTitle}</Typography>
            {finalized && quotation.single.quotation_status && (
              <StatusBadge
                status={quotation.single.quotation_status}
                type="quotation"
              />
            )}
          </Box>
        }
        actions={
          quotation.loading ? (
            <ButtonsSkeleton />
          ) : (
            <Box
              display="flex"
              flexDirection={isMobile ? 'column' : 'row'}
              alignItems={isMobile ? 'stretch' : 'center'}
              justifyContent="flex-end"
              width={isMobile ? '100%' : 'auto'}
              gap={1}>
              {/* Edit Mode: Cancel + Save */}
              {edit && (
              <Box
                display="flex"
                gap={1}
                width={isMobile ? '100%' : 'auto'}>
                <Tooltip title={TOOLTIPS.CANCEL_ESC}>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={handleCancel}
                    sx={{ flex: isMobile ? 1 : 'unset' }}>
                    Annuleren
                  </Button>
                </Tooltip>
                <Tooltip title={TOOLTIPS.SAVE_SHORTCUT}>
                  <Button
                    size="small"
                    endIcon={<Save />}
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={!form.formState.isValid}
                    sx={{ flex: isMobile ? 1 : 'unset' }}>
                    Offerte opslaan
                  </Button>
                </Tooltip>
              </Box>
            )}
            {/* Non-Edit, Not Finalized: Secondary actions + Primary action */}
            {!edit && !finalized && (
              <Box
                display="flex"
                gap={1}
                width={isMobile ? '100%' : 'auto'}
                flexDirection={isMobile ? 'column' : 'row'}
                flexWrap="wrap">
                {!isMobile ? (
                  <>
                    <Tooltip title={TOOLTIPS.DUPLICATE_QUOTATION}>
                      <Button
                        size="small"
                        variant="outlined"
                        endIcon={<ContentCopy />}
                        onClick={handleDuplicate}>
                        Dupliceren
                      </Button>
                    </Tooltip>
                    <Tooltip title={TOOLTIPS.EDIT_QUOTATION}>
                      <Button
                        size="small"
                        endIcon={<Edit />}
                        variant="outlined"
                        onClick={() => setEdit(true)}>
                        Offerte bewerken
                      </Button>
                    </Tooltip>
                    <Tooltip title="Bekijk een voorvertoning van de offerte">
                      <Button
                        size="small"
                        endIcon={<Visibility />}
                        variant="outlined"
                        onClick={handlePreview}>
                        Voorbeeld
                      </Button>
                    </Tooltip>
                  </>
                ) : (
                  <>
                    <Tooltip title={TOOLTIPS.DUPLICATE_QUOTATION}>
                      <Button
                        size="small"
                        variant="outlined"
                        endIcon={<ContentCopy />}
                        onClick={handleDuplicate}
                        sx={{ width: '100%' }}>
                        Dupliceren
                      </Button>
                    </Tooltip>
                    <Tooltip title={TOOLTIPS.EDIT_QUOTATION}>
                      <Button
                        size="small"
                        endIcon={<Edit />}
                        variant="outlined"
                        onClick={() => setEdit(true)}
                        sx={{ width: '100%' }}>
                        Offerte bewerken
                      </Button>
                    </Tooltip>
                    <Tooltip title="Bekijk een voorvertoning van de offerte">
                      <Button
                        size="small"
                        endIcon={<Visibility />}
                        variant="outlined"
                        onClick={handlePreview}
                        sx={{ width: '100%' }}>
                        Voorbeeld
                      </Button>
                    </Tooltip>
                  </>
                )}
                <Tooltip title={TOOLTIPS.FINALIZE_QUOTATION}>
                  <Button
                    size="small"
                    variant="contained"
                    endIcon={<Check />}
                    onClick={handleFinalize}
                    sx={{ width: isMobile ? '100%' : 'auto' }}>
                    Maak definitief
                  </Button>
                </Tooltip>
              </Box>
            )}
            {/* Finalized: Secondary actions + Primary action */}
            {finalized && (
              <Box
                display="flex"
                gap={1}
                width={isMobile ? '100%' : 'auto'}
                flexWrap="wrap">
                {!isMobile ? (
                  <>
                    <Tooltip
                      title={
                        isStatusTerminal
                          ? 'Status kan niet meer gewijzigd worden'
                          : 'Status van de offerte wijzigen'
                      }>
                      <span>
                        <Button
                          size="small"
                          variant="outlined"
                          endIcon={<SwapHoriz />}
                          onClick={() => setStatusDialogOpen(true)}
                          disabled={isStatusTerminal}>
                          Status wijzigen
                        </Button>
                      </span>
                    </Tooltip>
                    <Tooltip
                      title={
                        !billing.isPro
                          ? 'Upgrade naar PRO om documenten via e-mail te versturen'
                          : !company.current_company?.can_send_emails
                          ? TOOLTIPS.CONFIGURE_SMTP_FIRST
                          : TOOLTIPS.SEND_QUOTATION_EMAIL
                      }>
                      <span>
                        <Button
                          size="small"
                          variant="outlined"
                          endIcon={<Email />}
                          onClick={handleSendEmail}
                          disabled={!company.current_company?.can_send_emails || !billing.isPro}>
                          Versturen
                        </Button>
                      </span>
                    </Tooltip>
                    <Tooltip title={TOOLTIPS.DUPLICATE_QUOTATION}>
                      <Button
                        size="small"
                        variant="outlined"
                        endIcon={<ContentCopy />}
                        onClick={handleDuplicate}>
                        Dupliceren
                      </Button>
                    </Tooltip>
                    <Tooltip
                      title={
                        !company.current_company?.has_bank_account
                          ? TOOLTIPS.ADD_BANK_ACCOUNT_FIRST
                          : TOOLTIPS.DOWNLOAD_QUOTATION_PDF
                      }>
                      <span>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => quotation.downloadPdf()}
                          endIcon={<Download />}
                          disabled={!company.current_company?.has_bank_account}>
                          Download
                        </Button>
                      </span>
                    </Tooltip>
                  </>
                ) : (
                  <>
                    <Tooltip
                      title={
                        isStatusTerminal
                          ? 'Status kan niet meer gewijzigd worden'
                          : 'Status van de offerte wijzigen'
                      }>
                      <span style={{ flex: '1 1 calc(50% - 4px)', minWidth: 0 }}>
                        <Button
                          size="small"
                          variant="outlined"
                          endIcon={<SwapHoriz />}
                          onClick={() => setStatusDialogOpen(true)}
                          disabled={isStatusTerminal}
                          fullWidth>
                          Status wijzigen
                        </Button>
                      </span>
                    </Tooltip>
                    <Tooltip
                      title={
                        !billing.isPro
                          ? 'Upgrade naar PRO om documenten via e-mail te versturen'
                          : !company.current_company?.can_send_emails
                          ? TOOLTIPS.CONFIGURE_SMTP_FIRST
                          : TOOLTIPS.SEND_QUOTATION_EMAIL
                      }>
                      <span style={{ flex: '1 1 calc(50% - 4px)', minWidth: 0 }}>
                        <Button
                          size="small"
                          variant="outlined"
                          endIcon={<Email />}
                          onClick={handleSendEmail}
                          disabled={!company.current_company?.can_send_emails || !billing.isPro}
                          fullWidth>
                          Versturen
                        </Button>
                      </span>
                    </Tooltip>
                    <Tooltip title={TOOLTIPS.DUPLICATE_QUOTATION}>
                      <Button
                        size="small"
                        variant="outlined"
                        endIcon={<ContentCopy />}
                        onClick={handleDuplicate}
                        sx={{ flex: '1 1 calc(50% - 4px)', minWidth: 0 }}>
                        Dupliceren
                      </Button>
                    </Tooltip>
                    <Tooltip
                      title={
                        !company.current_company?.has_bank_account
                          ? TOOLTIPS.ADD_BANK_ACCOUNT_FIRST
                          : TOOLTIPS.DOWNLOAD_QUOTATION_PDF
                      }>
                      <span style={{ flex: '1 1 calc(50% - 4px)', minWidth: 0 }}>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => quotation.downloadPdf()}
                          endIcon={<Download />}
                          disabled={!company.current_company?.has_bank_account}
                          fullWidth>
                          Download
                        </Button>
                      </span>
                    </Tooltip>
                  </>
                )}
                <Tooltip title={TOOLTIPS.CREATE_INVOICE_FROM_QUOTATION}>
                  <Button
                    size="small"
                    variant="contained"
                    endIcon={<InvoiceIcon />}
                    onClick={handleConvertToInvoice}
                    sx={{ flex: isMobile ? '1 1 calc(50% - 4px)' : '0 0 auto', minWidth: isMobile ? 0 : 'auto' }}>
                    Maak factuur
                  </Button>
                </Tooltip>
              </Box>
            )}
            </Box>
          )
        }
        fixedActions={edit && isEditable}>
        {/* Display status history when quotation is finalized and has status */}
        {finalized && quotation.single.quotation_status && statusHistory.length > 0 && (
          <QuotationStatusHistory history={statusHistory} />
        )}
        {/* Display related documents */}
        <RelatedDocuments document={quotation.single} />
        <UpdateInvoiceForm documentType="quotation" />
        <SendEmailForm
          open={emailFormOpen}
          onClose={() => setEmailFormOpen(false)}
          documentId={parseInt(id ?? '')}
          documentType="quotation"
          defaultValues={{
            recipient_email: quotation.single.client?.email || '',
            recipient_name: quotation.single.client?.name || '',
            attach_pdf: true,
          }}
          onSuccess={async () => {
            // Refresh quotation data to get updated status
            const updatedQuotation = await quotation.getSingle(parseInt(id ?? ''))
            form.reset(updatedQuotation)
          }}
        />
        <QuotationStatusChangeDialog
          open={statusDialogOpen}
          currentStatus={quotation.single.quotation_status}
          onClose={() => setStatusDialogOpen(false)}
          onSubmit={handleStatusChange}
          isFinalized={finalized}
        />
      </PageWrapper>
    </OvFormProvider>
  )
})
